import React, { createContext, FC, memo, ReactNode, useCallback, useContext, useEffect } from 'react';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { LocalizationsDTO } from 'src/api/dtos';
import { usePrevious } from 'src/shared/hooks';
import { mapLanguageIdEnums, NestedKeyOf } from 'src/shared/utils';
import { RootState } from 'src/store/types';
import { getFirstDayOfWeek } from 'src/store/user/user.selectors';

import {
  TranslationFunction,
  translations,
  TranslationsType,
  TranslationSubstituteType,
} from './TranslationProvider.types';

const translate = (
  localizations: LocalizationsDTO,
  path: NestedKeyOf<TranslationsType> | number,
  substitute?: TranslationSubstituteType,
): string => {
  if (!localizations) {
    console.warn('The localization strings could not be loaded.');
    return path.toString();
  }

  // If path is a number, we skip looking it up in `translations`
  if (typeof path === 'number') {
    const translation = localizations[path];
    if (!translation) {
      console.warn(`The translation string for key [${path}] could not be found.`);
      return path.toString();
    }
    return translation;
  }

  const key = get(translations, path) as number;

  if (!key || !localizations[key]) {
    console.warn(`The translation string for [${path}: ${key}] could not be found.`);
    return path.toString();
  }

  if (substitute !== undefined && substitute !== null) {
    if (isObject(substitute)) {
      return Object.entries(substitute).reduce((str, [k, value]) => {
        const bracketRegex = new RegExp(`[${k}]]`, 'g');
        const curlyBracketsRegex = new RegExp(`{${k}}`, 'g');

        return str.replace(bracketRegex, value).replace(curlyBracketsRegex, value);
      }, localizations[key]);
    }

    return localizations[key].replace(/\[(.+?)]]/g, substitute.toString()).replace(/{.*?}/g, substitute.toString());
  }

  return localizations[key];
};

type TranslationProps = { t: TranslationFunction };

const TranslationContext = createContext<TranslationProps>({} as TranslationProps);

export const useTranslate = (): TranslationProps => useContext<TranslationProps>(TranslationContext);

export const TranslationProvider: FC<{ children: ReactNode }> = memo(({ children }) => {
  const { data: localizations } = useSelector((state: RootState) => state.localizations);
  const { preferences } = useSelector((state: RootState) => state.user);
  const previousPreferences = usePrevious(preferences);
  const firstDayOfWeek = useSelector(getFirstDayOfWeek);

  const t = useCallback(
    (path: NestedKeyOf<TranslationsType> | number, substitute?: TranslationSubstituteType) =>
      translate(localizations, path, substitute),
    [localizations],
  );

  useEffect(() => {
    if (preferences && !previousPreferences) {
      const lang = mapLanguageIdEnums[preferences.languageId].substring(0, 2);
      moment.updateLocale(lang, { week: { dow: firstDayOfWeek } });
    }

    if (!preferences || !previousPreferences || preferences.languageId === previousPreferences.languageId) {
      return;
    }

    // reload page when language is changed to update all string values
    window.location.reload();
  }, [preferences?.languageId]);

  return <TranslationContext.Provider value={{ t }}>{children}</TranslationContext.Provider>;
});
